// A partner's pages and a customer's partner access (specs/partners M1, WEB-PRT, WEB-PAC): the customers and the
// stock of the selected partner organisation over listPartnerCustomers, createPartnerCustomer, listPartnerStock and
// moveStock, and the partner access change over updateOrganisation.
import { queryOptions } from "@tanstack/react-query";
import { apiClient, unwrap } from "../../api/client";
import type { components } from "../../api/generated/vision-api";
import { ApiError } from "../../api/problem";
import type { Role } from "../organisation/roles";

type T = (key: string, options?: Record<string, unknown>) => string;
type Membership = components["schemas"]["Membership"];

export type Customer = components["schemas"]["Organisation"];
export type CustomerCreate = components["schemas"]["CustomerCreate"];
export type StockDevice = components["schemas"]["Device"];
export type StockMoveResult = components["schemas"]["StockMoveResult"];
export type PartnerAccess = "manage" | "support" | "none";

/** At most this many devices move in one request (`StockMove.device_ids` maxItems). */
export const MOVE_MAX = 500;

/** Highest first. */
export const ACCESS_LEVELS: PartnerAccess[] = ["manage", "support", "none"];

const api = () => apiClient("customers");

/** Customers and Stock are in the navigation of a partner organisation's Owners and Admins (WEB-PRT-01). */
export function hasPartnerPages(m: Membership | undefined): boolean {
  return !!m && m.org_kind === "partner" && (m.role === "owner" || m.role === "admin");
}

export const customersPageQuery = (partnerId: string, cursor?: string) =>
  queryOptions({
    queryKey: ["org", partnerId, "/v1/partners/customers", "page", cursor ?? ""],
    queryFn: () =>
      unwrap(
        api().GET("/v1/partners/{partner_id}/customers", {
          params: { path: { partner_id: partnerId }, query: { limit: 100, ...(cursor ? { cursor } : {}) } },
        }),
      ),
  });

/** Every customer, for the Move picker: a partner has hundreds at most. */
export const allCustomersQuery = (partnerId: string) =>
  queryOptions({
    queryKey: ["org", partnerId, "/v1/partners/customers", "all"],
    queryFn: async () => {
      const items: Customer[] = [];
      let cursor: string | undefined;
      do {
        const page = await unwrap(
          api().GET("/v1/partners/{partner_id}/customers", {
            params: { path: { partner_id: partnerId }, query: { limit: 100, ...(cursor ? { cursor } : {}) } },
          }),
        );
        items.push(...page.items);
        cursor = page.next_cursor ?? undefined;
      } while (cursor);
      return items;
    },
  });

/** One page of the partner's stock; the Stock page loads them one after the other as the person asks. */
export function stockPage(partnerId: string, cursor?: string) {
  return unwrap(
    api().GET("/v1/partners/{partner_id}/stock", {
      params: { path: { partner_id: partnerId }, query: { limit: 100, ...(cursor ? { cursor } : {}) } },
    }),
  );
}

export const stockKey = (partnerId: string) => ["org", partnerId, "/v1/partners/stock"] as const;

export function createCustomer(partnerId: string, body: CustomerCreate) {
  return unwrap(
    api().POST("/v1/partners/{partner_id}/customers", { params: { path: { partner_id: partnerId } }, body }),
  );
}

export function moveStock(partnerId: string, deviceIds: string[], orgId: string) {
  return unwrap(
    api().POST("/v1/partners/{partner_id}/stock/move", {
      params: { path: { partner_id: partnerId } },
      body: { device_ids: deviceIds, org_id: orgId },
    }),
  );
}

export type NewCustomer = { name: string; country: string; locale: "en" | "ro"; planId: string; ownerEmail: string };

/** The body of createPartnerCustomer, or the i18n key of what is wrong with the form (WEB-PRT-03). */
export function customerBody(f: NewCustomer): { ok: true; body: CustomerCreate } | { ok: false; error: string } {
  const name = f.name.trim();
  const email = f.ownerEmail.trim();
  const country = f.country.trim().toUpperCase();
  if (!name || name.length > 200) return { ok: false, error: "partners.new.error.name" };
  if (!/^[A-Z]{2}$/.test(country)) return { ok: false, error: "partners.new.error.country" };
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return { ok: false, error: "partners.new.error.email" };
  return {
    ok: true,
    body: { name, country, locale: f.locale, owner_email: email, plan_id: f.planId || "free" },
  };
}

/** Stock moves only to a customer the partner manages (StockMove.org_id). */
export function movableTo(customers: readonly Customer[]): Customer[] {
  return customers.filter((c) => c.partner_access === "manage");
}

/** The move's result in words (WEB-PRT-04): how many moved, and each refused device with its reason. */
export function moveSummary(
  r: StockMoveResult,
  label: (deviceId: string) => string,
  t: T,
): { text: string; refused: { device: string; reason: string }[] } {
  return {
    text:
      r.refused === 0
        ? t("partners.stock.moved", { count: r.moved })
        : t("partners.stock.movedSome", { count: r.moved, refused: r.refused }),
    refused: r.results
      .filter((x) => x.status !== "moved")
      .map((x) => ({ device: label(x.device_id), reason: t(`partners.stock.reason.${x.status}`) })),
  };
}

/**
 * The partner access levels a member may choose (WEB-PAC-02): only the Owner changes it; lowering is always offered,
 * raising too since only the Owner gets here. Nobody else is offered anything.
 */
export function accessChoices(role: Role | undefined, current: PartnerAccess): PartnerAccess[] {
  if (role !== "owner") return [];
  return ACCESS_LEVELS.filter((l) => l !== current);
}

/** Whether going from one level to another takes something away from the partner. */
export function isLowering(from: PartnerAccess, to: PartnerAccess): boolean {
  return ACCESS_LEVELS.indexOf(to) > ACCESS_LEVELS.indexOf(from);
}

// The partner access change waits across the sign-in redirect when the server asks for a fresh sign-in, and is retried
// once on return (WEB-PAC-02). sessionStorage keeps it to this tab; it holds no secret.
const PENDING = "creavision.partnerAccess.pending";

export function rememberPendingAccess(orgId: string, level: PartnerAccess): void {
  try {
    sessionStorage.setItem(PENDING, JSON.stringify({ orgId, level }));
  } catch {
    // Without storage the person makes the change again after signing in.
  }
}

/** The change waiting for this organisation, removed as it is read so it is tried once only. */
export function takePendingAccess(orgId: string): PartnerAccess | undefined {
  try {
    const raw = sessionStorage.getItem(PENDING);
    if (!raw) return undefined;
    sessionStorage.removeItem(PENDING);
    const p = JSON.parse(raw) as { orgId?: unknown; level?: unknown };
    return p.orgId === orgId && ACCESS_LEVELS.includes(p.level as PartnerAccess)
      ? (p.level as PartnerAccess)
      : undefined;
  } catch {
    return undefined;
  }
}

export function isReauth(e: unknown): boolean {
  return e instanceof ApiError && e.code === "reauth_required";
}

export function partnerError(e: unknown, t: T): string {
  if (!(e instanceof ApiError)) return t("partners.error.unknown");
  if (e.code === "reauth_required") return t("partners.error.reauth");
  if (e.status === 403) return t("partners.error.forbidden");
  if (e.status === 409) return t("partners.error.conflict");
  return e.message || t("partners.error.unknown");
}
